import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Music, DollarSign, Rocket, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = '';  // Use relative URLs with proxy

const ArtistComingSoon = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = searchParams.get('redirect');
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    artist_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email) {
      toast({
        title: "Missing fields",
        description: "Please fill in your name and email",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/waitlist/artist`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast({
          title: "Success!",
          description: response.data.message
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to join waitlist. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle redirect back to cafe after signup
  const handleGoToCafe = () => {
    if (redirectTo) {
      navigate(redirectTo);
    } else {
      navigate('/cafe');
    }
  };

  return (
    <>
      <Helmet>
        <title>Artist Access Beta | Mic Check Millionaire</title>
        <meta 
          name="description" 
          content="Join the Mic Check Millionaire Artist Access Beta. Upload your music for live reviews, earn from real-time feedback, and connect with verified reviewers."
        />
        <meta 
          name="keywords" 
          content="artist submission platform, live music review, indie artist beta, monetize feedback, music discovery network"
        />
        
        {/* Open Graph */}
        <meta property="og:title" content="Mic Check Millionaire Artist Access Beta | Join Early" />
        <meta property="og:description" content="Artist access is now in private beta. Lock in your spot before we open to the public." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/artist-beta-og.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Artist Beta Access | Mic Check Millionaire" />
        <meta name="twitter:description" content="Join early and lock in your spot for artist dashboard access." />
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>
          
          <div className="relative container mx-auto px-4 py-20 md:py-32">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F2D857]/10 border border-[#F2D857]/30 rounded-full mb-8">
                <span className="text-[#F2D857] font-bold text-sm">PRIVATE BETA</span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                You&apos;re <span className="text-[#F2D857]">Early</span> — <br />
                and That&apos;s <span className="text-[#F2D857]">Powerful</span>.
              </h1>

              {/* Subtext */}
              <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
                We&apos;re finalizing artist access tools for live reviews, uploads, and monetization. 
                You&apos;re already one step ahead — you&apos;ll be first to know when we open.
              </p>

              {/* Waitlist Form or Success Message */}
              {!submitted ? (
                <div className="max-w-2xl mx-auto bg-gray-900/50 backdrop-blur-sm border border-[#F2D857]/20 rounded-2xl p-8 md:p-12">
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">Join Early Access</h2>
                  <p className="text-gray-400 mb-8">Be the first to know when artist submissions open</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#F2D857] focus:outline-none transition text-white"
                        placeholder="Your full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#F2D857] focus:outline-none transition text-white"
                        placeholder="your@email.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Artist/Stage Name <span className="text-gray-500">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={formData.artist_name}
                        onChange={(e) => setFormData({...formData, artist_name: e.target.value})}
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#F2D857] focus:outline-none transition text-white"
                        placeholder="Your stage name"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Joining...
                        </>
                      ) : (
                        'Join the Waitlist'
                      )}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="max-w-2xl mx-auto bg-green-900/20 border border-green-500/30 rounded-2xl p-8 md:p-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">You&apos;re On The List!</h2>
                  <p className="text-lg text-gray-300 mb-6">
                    You&apos;re officially on the list. You&apos;ll be the first to know when artist submissions open.
                  </p>
                  <p className="text-gray-400 mb-6">
                    Check your email for confirmation and a magic link to join the community discussions.
                  </p>
                  
                  {/* Go to Cafe Button - shows when user came from cafe */}
                  {redirectTo === '/cafe' && (
                    <button
                      onClick={handleGoToCafe}
                      className="inline-flex items-center gap-2 bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                    >
                      Go to The Cafe
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  )}
                  
                  {!redirectTo && (
                    <Link
                      to="/cafe"
                      className="inline-flex items-center gap-2 text-[#F2D857] hover:text-[#F2D857]/80 font-medium transition-colors"
                    >
                      Visit The Millionaire Cafe
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="bg-gray-900 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="text-center p-8 bg-black border border-gray-800 rounded-xl hover:border-[#F2D857]/50 transition-all duration-300">
                <div className="w-16 h-16 bg-[#F2D857]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Music className="w-8 h-8 text-[#F2D857]" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Upload Your Music</h3>
                <p className="text-gray-400 leading-relaxed">
                  Submit your tracks for live reviews from verified curators and industry professionals.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="text-center p-8 bg-black border border-gray-800 rounded-xl hover:border-[#F2D857]/50 transition-all duration-300">
                <div className="w-16 h-16 bg-[#F2D857]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DollarSign className="w-8 h-8 text-[#F2D857]" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Earn From Feedback</h3>
                <p className="text-gray-400 leading-relaxed">
                  Get real-time, actionable feedback that helps you grow while supporting the community.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="text-center p-8 bg-black border border-gray-800 rounded-xl hover:border-[#F2D857]/50 transition-all duration-300">
                <div className="w-16 h-16 bg-[#F2D857]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Rocket className="w-8 h-8 text-[#F2D857]" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Connect & Grow</h3>
                <p className="text-gray-400 leading-relaxed">
                  Network with verified reviewers and curators who can elevate your music career.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="bg-black py-16 border-t border-gray-800">
          <div className="container mx-auto px-4 text-center">
            <p className="text-xl text-gray-300 mb-6">
              You&apos;re part of something bigger. Keep an eye out for your invite — and join the conversation in the <Link to="/cafe" className="text-[#F2D857] hover:underline font-bold">Millionaire Café</Link> while you wait.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link
                to="/cafe"
                className="px-8 py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-lg transition"
              >
                Visit Millionaire Café
              </Link>
              <Link
                to="/reviewers"
                className="px-8 py-3 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-lg transition"
              >
                Browse Reviewers
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArtistComingSoon;
